import { useEffect, useState, useContext } from "react";
import { AuthContext } from "../../../authContext";
import { useTranslation } from "react-i18next";
import AlertsCustom from "../../../components/Alerts";
import ToggleSwitch from "../../../components/ToggleSwitch";
import ChangeStatus from "../../../components/ChangeStatus";
import "./assistants.css";
import LoadingSpinner from "../../../components/LoadingSpinner";
import ConfirmDeleteModal2 from "../../../components/DeletAssitant";

function AssistantContent({ onTogglePublishInput }) {
  const { t: tManagement } = useTranslation("management");
  const { t: tCommon } = useTranslation("common");
  const { t: tSelfAssist } = useTranslation("assistantConfiguration");
  const { accessToken } = useContext(AuthContext);
  const [assistants, setAssistants] = useState([]);
  const [expandedAssistants, setExpandedAssistants] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [statusMap, setStatusMap] = useState({});
  const [tempUsersList, setTempUsersList] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [allGroups, setAllGroups] = useState([]);
  const [tempGroupsList, setTempGroupsList] = useState([]);
  const [activePanel, setActivePanel] = useState(null); // 'user' | 'admin' | 'group'
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [panelSearchTerm, setPanelSearchTerm] = useState("");
  const [emailToAdd, setEmailToAdd] = useState("");
  const [allowedDomains, setAllowedDomains] = useState([]);
  const [alertTitle, setAlertTitle] = useState("");
  const [alertType, setAlertType] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [emailToRemove, setEmailToRemove] = useState("");
  const [groupIdToRemove, setGroupIdToRemove] = useState("");
  const [assistantIdToRemove, setAssistantIdToRemove] = useState("");
  const [pendingToggleAssistantId, setPendingToggleAssistantId] =
    useState(null);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/get_domains`)
      .then((res) => res.json())
      .then((data) =>
        setAllowedDomains(data.map((d) => d.dominio.toLowerCase().trim())),
      )
      .catch((err) => console.error("Erro ao buscar domínios:", err));
  }, []);

  useEffect(() => {
    if (activePanel !== "user" && activePanel !== "admin") return;

    setLoadingUsers(true);
    fetch(`${process.env.REACT_APP_API_URL}/get_list_users`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })
      .then((res) => res.json())
      .then((data) => setAllUsers(data))
      .catch((err) => {
        console.error("Erro ao carregar usuários:", err);
        setAlertTitle("Erro");
        setAlertType("error");
        setAlertMessage(tSelfAssist("erro_usuarios"));
        setShowAlert(true);
      })
      .finally(() => setLoadingUsers(false));
  }, [activePanel, accessToken, tSelfAssist]);

  useEffect(() => {
    // FR-006c: buscado sempre no mount (não só ao abrir o painel "Grupos"),
    // pois a lista mista de vinculados (linha ~700) precisa resolver
    // nome/tipo dos group_ids do assistente mesmo com o painel fechado.
    fetch(`${process.env.REACT_APP_API_URL}/get_all_groups`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then((res) => res.json())
      .then((data) => setAllGroups(data.groups || []))
      .catch((err) => console.error("Erro ao carregar grupos:", err));
  }, [accessToken]);

  const isAllowedDomain = (email) => {
    const parts = email.split("@");
    if (parts.length < 2) return false;
    return allowedDomains.includes(parts[1].toLowerCase().trim());
  };

  const handleTogglePanelUser = async (user) => {
    const isAlreadyAdded = tempUsersList.some((u) => u.email === user.email);
    if (isAlreadyAdded) {
      setTempUsersList((prev) => prev.filter((u) => u.email !== user.email));
      return;
    }
    setTempUsersList((prev) => [
      ...prev,
      user.user ? user : { email: user.email },
    ]);
  };

  const handlePanelItemKeyDown = (event, user) => {
    if (event.key !== "Enter" && event.key !== " ") return;

    event.preventDefault();
    handleTogglePanelUser(user);
  };

  const handleTogglePanelGroup = (group) => {
    const isAlreadyAdded = tempGroupsList.some((g) => g._id === group._id);
    setTempGroupsList((prev) =>
      isAlreadyAdded
        ? prev.filter((g) => g._id !== group._id)
        : [...prev, group],
    );
  };

  const handlePanelGroupKeyDown = (event, group) => {
    if (event.key !== "Enter" && event.key !== " ") return;

    event.preventDefault();
    handleTogglePanelGroup(group);
  };

  const handleSavePanelUsers = async () => {
    if (!expandedAssistants || tempUsersList.length === 0) return;

    const previousUsers = expandedAssistants.users;
    const existingEmails = new Set(previousUsers.map((u) => u.email));
    const newUsers = tempUsersList.filter((u) => !existingEmails.has(u.email));
    setExpandedAssistants((prev) =>
      prev ? { ...prev, users: [...prev.users, ...newUsers] } : prev,
    );
    setAssistants((prev) =>
      prev.map((a) =>
        a.id === expandedAssistants.id
          ? { ...a, users: [...a.users, ...newUsers] }
          : a,
      ),
    );
    setTempUsersList([]);
    setActivePanel(null);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/put_update_users`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            assistant_id: expandedAssistants.id,
            users: tempUsersList,
            role: activePanel,
          }),
        },
      );
      if (response.ok) {
        const updatedUsers = await response.json();
        setAllUsers(updatedUsers);
        setAlertTitle("Sucesso");
        setAlertType("success");
        setAlertMessage(tSelfAssist("add_sucess"));
        setShowAlert(true);
      } else {
        setExpandedAssistants((prev) =>
          prev ? { ...prev, users: previousUsers } : prev,
        );
        setAssistants((prev) =>
          prev.map((a) =>
            a.id === expandedAssistants.id ? { ...a, users: previousUsers } : a,
          ),
        );
        setAlertTitle("Erro");
        setAlertType("error");
        setAlertMessage(tSelfAssist("erro_salvar"));
        setShowAlert(true);
      }
    } catch (error) {
      console.error("Erro ao salvar usuários:", error);
      setExpandedAssistants((prev) =>
        prev ? { ...prev, users: previousUsers } : prev,
      );
      setAssistants((prev) =>
        prev.map((a) =>
          a.id === expandedAssistants.id ? { ...a, users: previousUsers } : a,
        ),
      );
      setAlertTitle("Erro");
      setAlertType("error");
      setAlertMessage(tSelfAssist("erro_salvar"));
      setShowAlert(true);
    }
  };

  const formatUserName = (email) => {
  const namePart = email.split("@")[0];
  return namePart
    .split(".")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

  const handleAddByEmail = async () => {
    const email = emailToAdd.trim();
    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!isValidEmail) {
      setAlertTitle("Erro");
      setAlertType("error");
      setAlertMessage(tSelfAssist("email_valido"));
      setShowAlert(true);
      return;
    }
    if (!isAllowedDomain(email)) {
      setAlertTitle("Erro");
      setAlertType("error");
      setAlertMessage(tSelfAssist("dominio_valido"));
      setShowAlert(true);
      return;
    }
    const previousUsers = expandedAssistants.users;
    const alreadyExists = previousUsers.some((u) => u.email === email);
    if (!alreadyExists) {
      setExpandedAssistants((prev) =>
        prev ? { ...prev, users: [...prev.users, { email }] } : prev,
      );
      setAssistants((prev) =>
        prev.map((a) =>
          a.id === expandedAssistants.id
            ? { ...a, users: [...a.users, { email }] }
            : a,
        ),
      );
    }
    setEmailToAdd("");
    setActivePanel(null);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/put_update_users`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            assistant_id: expandedAssistants.id,
            users: [{ email }],
            role: activePanel,
          }),
        },
      );
      if (response.ok) {
        const updatedUsers = await response.json();
        setAllUsers(updatedUsers);
        setAlertTitle("Sucesso");
        setAlertType("success");
        setAlertMessage(tSelfAssist("add_sucess"));
        setShowAlert(true);
      } else {
        setExpandedAssistants((prev) =>
          prev ? { ...prev, users: previousUsers } : prev,
        );
        setAssistants((prev) =>
          prev.map((a) =>
            a.id === expandedAssistants.id ? { ...a, users: previousUsers } : a,
          ),
        );
        setAlertTitle("Erro");
        setAlertType("error");
        setAlertMessage(tSelfAssist("erro_salvar"));
        setShowAlert(true);
      }
    } catch (error) {
      setExpandedAssistants((prev) =>
        prev ? { ...prev, users: previousUsers } : prev,
      );
      setAssistants((prev) =>
        prev.map((a) =>
          a.id === expandedAssistants.id ? { ...a, users: previousUsers } : a,
        ),
      );
      setAlertTitle("Erro");
      setAlertType("error");
      setAlertMessage(tSelfAssist("erro_salvar"));
      setShowAlert(true);
    }
  };

  const handleRemoveUser = async (emailToRemove, assistantId) => {
    // Remove localmente antes da requisição
    setAssistants((prev) =>
      prev.map((a) =>
        a.id === assistantId
          ? {
              ...a,
              users: a.users.filter((u) => u.email !== emailToRemove),
            }
          : a,
      ),
    );

    setExpandedAssistants((prev) =>
      prev?.id === assistantId
        ? { ...prev, users: prev.users.filter((u) => u.email !== emailToRemove) }
        : prev,
    );

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/put_delete_user_from_assistant`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            assistant_id: assistantId,
            email: emailToRemove,
          }),
        },
      );

      const result = await response.json();

      if (!response.ok || !result.status) {
        throw new Error(result.message || "Erro ao remover usuário");
      }
      // Nenhuma ação extra necessária, já removido localmente
    } catch (err) {
      console.error("Erro ao remover usuário:", err.message);
      // Opcional: reverter remoção local ou mostrar alerta
    }
  };

  const handleSaveGroups = async (role) => {
    if (!expandedAssistants || tempGroupsList.length === 0) return;

    const previousGroupIds = expandedAssistants.group_ids || [];
    const previousAdminGroupIds = expandedAssistants.admin_group_ids || [];
    const newGroupIds = tempGroupsList
      .map((g) => g._id)
      .filter((id) => !previousGroupIds.includes(id));
    const updatedGroupIds = [...previousGroupIds, ...newGroupIds];
    // role="admin" espelha put_update_users: grupo vira admin ADEMAIS de
    // usuário comum, nunca só admin (mesma regra do backend).
    const updatedAdminGroupIds =
      role === "admin"
        ? Array.from(
            new Set([...previousAdminGroupIds, ...tempGroupsList.map((g) => g._id)]),
          )
        : previousAdminGroupIds;

    const applyLocally = (groupIds, adminGroupIds) => {
      setExpandedAssistants((prev) =>
        prev ? { ...prev, group_ids: groupIds, admin_group_ids: adminGroupIds } : prev,
      );
      setAssistants((prev) =>
        prev.map((a) =>
          a.id === expandedAssistants.id
            ? { ...a, group_ids: groupIds, admin_group_ids: adminGroupIds }
            : a,
        ),
      );
    };

    applyLocally(updatedGroupIds, updatedAdminGroupIds);
    setTempGroupsList([]);
    setActivePanel(null);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/put_update_assistant_groups`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            assistant_id: expandedAssistants.id,
            group_ids: newGroupIds,
            role,
          }),
        },
      );

      if (!response.ok) {
        applyLocally(previousGroupIds, previousAdminGroupIds);
        setAlertTitle("Erro");
        setAlertType("error");
        setAlertMessage(tSelfAssist("erro_salvar"));
        setShowAlert(true);
      }
    } catch (error) {
      applyLocally(previousGroupIds, previousAdminGroupIds);
      setAlertTitle("Erro");
      setAlertType("error");
      setAlertMessage(tSelfAssist("erro_salvar"));
      setShowAlert(true);
    }
  };

  const handleRemoveGroupFromAssistant = async (groupIdToRemove, assistantId) => {
    setAssistants((prev) =>
      prev.map((a) =>
        a.id === assistantId
          ? { ...a, group_ids: (a.group_ids || []).filter((id) => id !== groupIdToRemove) }
          : a,
      ),
    );
    setExpandedAssistants((prev) =>
      prev?.id === assistantId
        ? { ...prev, group_ids: (prev.group_ids || []).filter((id) => id !== groupIdToRemove) }
        : prev,
    );

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/put_remove_group_from_assistant`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            assistant_id: assistantId,
            group_id: groupIdToRemove,
          }),
        },
      );

      if (!response.ok) {
        throw new Error("Erro ao remover grupo");
      }
    } catch (err) {
      console.error("Erro ao remover grupo:", err.message);
    }
  };

  useEffect(() => {
    const initialStatus = {};
    assistants.forEach((a) => {
      initialStatus[a.id] = a.status; // ou outro campo de status
      initialStatus[a.id] = a.status; // ou outro campo de status
    });
    setStatusMap(initialStatus);
  }, [assistants]);

  const toggleStatusLocally = (id) => {
    setStatusMap((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleToggleClick = (assistantId) => {
    // Open confirmation modal, don't change status yet
    setPendingToggleAssistantId(assistantId);
  };

  const confirmToggle = async () => {
    if (!pendingToggleAssistantId) return;
    const id = pendingToggleAssistantId;
    const newStatus = !statusMap[id];

    // Atualiza o status local imediatamente
    toggleStatusLocally(id);

    // Fecha o modal imediatamente após confirmar
    setPendingToggleAssistantId(null);

    try {
      // Try primary endpoint
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/put_update_status`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ assistant_id: id, status: newStatus }),
        },
      );

      let ok = false;
      if (res.ok) {
        const data = await res.json();
        ok = data?.status === true;
      }

      if (!ok) {
        // Se falhar, reverte o status local
        toggleStatusLocally(id);
        setAlertTitle("Erro");
        setAlertType("error");
        setAlertMessage(
          tCommon("frase_confirmar") + " - Falha ao atualizar status.",
        );
        setShowAlert(true);
      }
    } catch (e) {
      // Se falhar, reverte o status local
      toggleStatusLocally(id);
      setAlertTitle("Erro");
      setAlertType("error");
      setAlertMessage("Falha ao atualizar status.");
      setShowAlert(true);
    }
  };

  const cancelToggle = () => {
    // Simply close modal and keep the status unchanged
    setPendingToggleAssistantId(null);
  };

  const toggleExpand = (assistantId) => {
    setExpandedAssistants((prev) => {
      const updatedStates = {
        ...prev,
        [assistantId]: !prev[assistantId],
      };

      // Verifica se existe pelo menos um assistente aberto/expandido (true)
      const isAnyAssistantExpanded = Object.values(updatedStates).some(
        (status) => status === true,
      );

      // Comunica ao componente pai se ele deve ocultar (true) ou mostrar (false) o input
      if (onTogglePublishInput) {
        onTogglePublishInput(isAnyAssistantExpanded);
      }

      return updatedStates;
    });
  };

  useEffect(() => {
    const fetchAssistants = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/get_all_assistants`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          },
        );

        const data = await response.json();

        if (data.success) {
          setAssistants(data.assistants);
        } else {
          console.error("Erro ao buscar assistentes:", data.error);
        }
      } catch (error) {
        console.error("Erro na requisição:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAssistants();
  }, [accessToken]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (expandedAssistants) {
    if (activePanel) {
      return (
        <div className="expanded-area">
          <div className="expanded-area-header">
            <button
              className="btn-back"
              onClick={() => {
                setActivePanel(null);
                setPanelSearchTerm("");
                setTempUsersList([]);
                setTempGroupsList([]);
              }}
            >
              <img src="/imagens/arrow_back.svg" alt="Back" />
            </button>
            {(tempUsersList.length > 0 || tempGroupsList.length > 0) && (
              <button
                className="addUser-btn"
                onClick={() => {
                  if (tempUsersList.length > 0) handleSavePanelUsers();
                  if (tempGroupsList.length > 0) handleSaveGroups(activePanel);
                }}
              >
                {tCommon("save")}
              </button>
            )}
          </div>
          <div className="assistant-wrapper">
            <div className="assistant-area">
              <div className="assistant-icon">
                {expandedAssistants.icon ? (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: expandedAssistants.icon,
                    }}
                  />
                ) : (
                  <img
                    src="/imagens/AndroidLogo.svg"
                    alt={tCommon("assistantIcon", {
                      name: expandedAssistants.nome,
                    })}
                  />
                )}
              </div>
            </div>
            <div className="assistant-wrapper-user">
              <p className="assistant-name-mod">
                {expandedAssistants.nome || "Sem nome"}
              </p>
              <p className="assistant-owner">
                <div className="assistant-proprietario-info">
                  <div className="proprietario">
                    {tManagement("proprietario")}: {(expandedAssistants.propietario || "none").length > 20
                      ? `${(expandedAssistants.propietario || "none").slice(0, 20)}...`
                      : expandedAssistants.propietario || "none"}
                  </div>
                </div>
              </p>
            </div>
          </div>
          <div className="inline-add-panel">
            <div className="inline-add-panel-row">
              <div className="input-assistants input-assistants-inline">
                <input
                  className="modal-input-assistants"
                  type="text"
                  placeholder={tCommon("search")}
                  value={panelSearchTerm}
                  onChange={(e) => setPanelSearchTerm(e.target.value)}
                />
                <img
                  src="/imagens/search.svg"
                  alt={tCommon("searchIcon")}
                  className="modal-input-icon"
                />
              </div>
              {activePanel === "user" && (
                <div className="inline-add-email-row">
                  <input
                    className="modal-input-assistants modal-input-email"
                    type="text"
                    placeholder={tSelfAssist("add_by_email")}
                    value={emailToAdd}
                    onChange={(e) => setEmailToAdd(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleAddByEmail();
                    }}
                  />
                  <img
                    src="/imagens/e-mail_icon.svg"
                    alt={tCommon("searchIcon")}
                    className="modal-input-email-icon"
                    onClick={handleAddByEmail}
                  />
                </div>
              )}
            </div>
            {loadingUsers ? (
              <LoadingSpinner />
            ) : (
            <ul className="user-management-list">
              {/* Ponto 1 (2026-08-04): grupo passou a ser uma opção dentro
                  dos painéis "Usuários"/"Administradores" que já existiam —
                  não há mais um botão "Grupos" separado. Vincular pelo
                  painel "Administradores" grava role="admin" (handleSaveGroups). */}
              {allGroups
                .filter((g) => {
                  const linkedIds =
                    activePanel === "admin"
                      ? expandedAssistants.admin_group_ids || []
                      : expandedAssistants.group_ids || [];
                  return !linkedIds.includes(g._id);
                })
                .filter((g) =>
                  g.name.toLowerCase().includes(panelSearchTerm.toLowerCase()),
                )
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((group) => {
                  const isSelected = tempGroupsList.some(
                    (g) => g._id === group._id,
                  );
                  return (
                    <li
                      key={group._id}
                      className={`user-management-item selectable${isSelected ? " selected" : ""}`}
                      onClick={() => handleTogglePanelGroup(group)}
                      onKeyDown={(event) =>
                        handlePanelGroupKeyDown(event, group)
                      }
                      role="button"
                      tabIndex={0}
                      aria-pressed={isSelected}
                    >
                      <div className="item-wrapper">
                        <div className="modal-admin-avatar-popup">
                        {(
                          group.name?.[0]
                        ).toUpperCase()}
                      </div>
                        <div className="user-info-assistant">
                          <span className="modal-admin-name">
                            {group.name}
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        className={`inline-toggle-btn${isSelected ? " selected" : ""}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleTogglePanelGroup(group);
                        }}
                      >
                        <img
                          src={
                            isSelected
                              ? "/imagens/checked.svg"
                              : "/imagens/unchecked.svg"
                          }
                          alt={
                            isSelected
                              ? tManagement("remove_user_tooltip")
                              : tManagement("groups_btn")
                          }
                        />
                      </button>
                    </li>
                  );
                })}
              {Array.isArray(allUsers) &&
                allUsers
                  .sort((a, b) => a.email.localeCompare(b.email))
                  .filter((u) => !(activePanel === "admin" && u.role === "externo"))
                  .filter(
                    (u) =>
                      u.user
                        ?.toLowerCase()
                        .includes(panelSearchTerm.toLowerCase()) ||
                      u.email
                        .toLowerCase()
                        .includes(panelSearchTerm.toLowerCase()),
                  )
                  .map((user) => {
                    const isSelected = tempUsersList.some(
                      (u) => u.email === user.email,
                    );
                    return (
                      <li
                        key={user.email}
                        className={`user-management-item selectable${isSelected ? " selected" : ""}`}
                        onClick={() => handleTogglePanelUser(user)}
                        onKeyDown={(event) =>
                          handlePanelItemKeyDown(event, user)
                        }
                        role="button"
                        tabIndex={0}
                        aria-pressed={isSelected}
                      >
                        <div className="item-wrapper">
                          <div className="modal-admin-avatar-popup">
                            {(
                              user.user?.[0] ||
                              user.email?.[0] ||
                              "?"
                            ).toUpperCase()}
                          </div>
                          <div className="user-info-assistant">
                            <span className="modal-admin-name">
                              {user.user || user.email}
                            </span>
                            <span className="modal-admin-email">
                              {user.email}
                            </span>
                          </div>
                        </div>
                        <button
                          type="button"
                          className={`inline-toggle-btn${isSelected ? " selected" : ""}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleTogglePanelUser(user);
                          }}
                        >
                          <img
                            src={
                              isSelected
                                ? "/imagens/checked.svg"
                                : "/imagens/unchecked.svg"
                            }
                            alt={
                              isSelected
                                ? tManagement("remove_user_tooltip")
                                : tManagement("user_btn")
                            }
                          />
                        </button>
                      </li>
                    );
                  })}
            </ul>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="expanded-area">
        <div className="expanded-area-header">
          <button
            className="btn-back"
            onClick={() => setExpandedAssistants(null)}
          >
            <img src="/imagens/arrow_back.svg" alt="Back" />
          </button>
        </div>
        <div className="assistant-wrapper assistant-wrapper--with-actions">
          <div className="assistant-wrapper-responsive">
            <div className="assistant-area">
              <div className="assistant-icon">
                {expandedAssistants.icon ? (
                  <div
                    dangerouslySetInnerHTML={{ __html: expandedAssistants.icon }}
                  />
                ) : (
                  <img
                    src="/imagens/AndroidLogo.svg"
                    alt={tCommon("assistantIcon", {
                      name: expandedAssistants.nome,
                    })}
                  />
                )}
              </div>
            </div>
            <div className="assistant-wrapper-user">
              <p className="assistant-name-mod">
                {expandedAssistants.nome || "Sem nome"}
              </p>
              <p className="assistant-owner">
                <div className="assistant-proprietario-info">
                  <div className="proprietario">
                    {tManagement("proprietario")}: {(expandedAssistants.propietario || "none").length > 20
                      ? `${(expandedAssistants.propietario || "none").slice(0, 20)}...`
                      : expandedAssistants.propietario || "none"}
                  </div>
                </div>
              </p>
            </div>
          </div>
          <div className="assistant-addUser">
            <button
              className="addUser-btn"
              onClick={() => {
                setActivePanel("user");
                setPanelSearchTerm("");
                setTempUsersList([]);
                setTempGroupsList([]);
              }}
            >
              {tManagement("user_btn")}
            </button>
            <button
              className="admin-btn"
              onClick={() => {
                setActivePanel("admin");
                setPanelSearchTerm("");
                setTempUsersList([]);
                setTempGroupsList([]);
              }}
            >
              {tManagement("adm_adc")}
            </button>
          </div>
        </div>
        <div className="input-assistants">
          <input
            className="modal-input-assistants"
            type="text"
            placeholder={tCommon("search")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <img
            src="/imagens/search.svg"
            alt={tCommon("searchIcon")}
            className="modal-input-icon"
          />
        </div>
        <ul className="user-management-list">
          {(() => {
            // FR-006c: grupos vinculados (expandedAssistants.group_ids)
            // aparecem misturados com os usuários individuais na mesma lista.
            const linkedGroups = (expandedAssistants.group_ids || [])
              .map((groupId) => allGroups.find((g) => g._id === groupId))
              .filter(Boolean);

            const mixedItems = [
              ...expandedAssistants.users.map((user) => ({
                kind: "user",
                key: user.email,
                label: user.email,
                user,
              })),
              ...linkedGroups.map((group) => ({
                kind: "group",
                key: group._id,
                label: group.name,
                group,
              })),
            ]
              .filter((item) =>
                item.label.toLowerCase().includes(searchQuery.toLowerCase()),
              )
              .sort((a, b) => a.label.localeCompare(b.label));

            if (mixedItems.length === 0) {
              return (
                <li className="user-management-empty">
                  {tManagement("no_users")}
                </li>
              );
            }
            return mixedItems.map((item) =>
              item.kind === "group" ? (
                <li
                  key={item.key}
                  className="user-management-item user-management-item--group"
                >
                  <div className="item-wrapper">
                    <div className="modal-admin-avatar-popup">
                      {(
                        item.group.name?.[0]
                      ).toUpperCase()}
                    </div>
                    <div className="user-info-assistant">
                      <span className="modal-admin-name">
                        {item.group.name}
                      </span>
                    </div>
                  </div>
                  <button
                    className="remove-user-button"
                    title={tManagement("remove_user_tooltip")}
                    onClick={() => {
                      setGroupIdToRemove(item.group._id);
                      setAssistantIdToRemove(expandedAssistants.id);
                      setIsDeleteModalOpen(true);
                    }}
                  >
                    <img
                      src="/imagens/x-circle.svg"
                      alt={tManagement("remove_user_tooltip")}
                    />
                  </button>
                </li>
              ) : (
                <li key={item.key} className="user-management-item">
                  <div className="item-wrapper">
                    <div className="modal-admin-avatar-popup">
                      {(
                        item.user.user?.[0] ||
                        item.user.email?.[0] ||
                        "?"
                      ).toUpperCase()}
                    </div>
                    <div className="user-info-assistant">
                      <span className="modal-admin-name">
                        {formatUserName(item.user.email)}
                      </span>
                      <span className="modal-admin-email">
                        {item.user.email}
                      </span>
                    </div>
                  </div>
                  <button
                    className="remove-user-button"
                    title={tManagement("remove_user_tooltip")}
                    onClick={() => {
                      setEmailToRemove(item.user.email);
                      setAssistantIdToRemove(expandedAssistants.id);
                      setIsDeleteModalOpen(true);
                    }}
                  >
                    <img
                      src="/imagens/x-circle.svg"
                      alt={tManagement("remove_user_tooltip")}
                    />
                  </button>
                </li>
              ),
            );
          })()}
        </ul>
        {isDeleteModalOpen && (
          <ConfirmDeleteModal2
            isOpen={isDeleteModalOpen}
            onConfirm={() => {
              if (groupIdToRemove) {
                handleRemoveGroupFromAssistant(groupIdToRemove, assistantIdToRemove);
              } else {
                handleRemoveUser(emailToRemove, assistantIdToRemove);
              }
              setEmailToRemove("");
              setGroupIdToRemove("");
              setIsDeleteModalOpen(false);
            }}
            onClose={() => {
              setEmailToRemove("");
              setGroupIdToRemove("");
              setIsDeleteModalOpen(false);
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div className="assistant-list">
      <div className="search-container-management">
        <input
          type="text"
          className="publish-input"
          placeholder={tManagement("search")}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <img
          src="/imagens/search.svg"
          alt={tCommon("searchIcon")}
          className="publish-search-icon"
        />
      </div>
      {assistants
        .filter((assistant) =>
          assistant.nome.toLowerCase().includes(searchTerm.toLowerCase()),
        )
        .sort((a, b) => a.nome.localeCompare(b.nome))
        .map((assistant) => (
          <div className="assistant-item-container" key={assistant.id}
          >
            <div className="assistant-wrapper">
              <div className="assistant-area">
                <div className="assistant-icon">
                  {assistant.icon ? (
                    <div dangerouslySetInnerHTML={{ __html: assistant.icon }} />
                  ) : (
                    <img
                      src="/imagens/AndroidLogo.svg"
                      alt={tCommon("assistantIcon", { name: assistant.nome })}
                    />
                  )}
                </div>
              </div>
              <div className="assistant-wrapper-user">
                <p className="assistant-name-mod">{assistant.nome || "Sem nome"}</p>
                <p className="assistant-owner">
                  <div className="assistant-proprietario-info">
                    <div className="proprietario">
                      {tManagement("proprietario")}: {assistant.propietario || "none"}
                    </div>
                  </div>
                </p>
              </div>
              <p className="assistant-type">{assistant.type || "Sem tipo"}</p>

              <button
                className="btn-assistant-details"
                onClick={() => setExpandedAssistants(assistant)}
              >
                <img
                  src="/imagens/users-rigth.svg"
                  className="management-painel-btn-logo"
                  alt=""
                />
              </button>
            </div>
          </div>
        ))}
    </div>
  );
}

export default AssistantContent;
